import { useState } from "react";
import { Link, useLocation, useNavigate, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, ShieldCheck } from "lucide-react";
import { useAuth, homeFor } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Field } from "@/components/Common";
import { ForgotPasswordDialog } from "@/components/ForgotPasswordDialog";
import { BrandMark } from "@/components/LogoUploader";
import { useBranding } from "@/lib/branding";

export default function Login() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { logo } = useBranding();

  if (user) return <Navigate to={homeFor(user)} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const u = await login(email, password);
      toast.success(`Benvenuto, ${u.name}`);
      nav(loc.state?.from || homeFor(u), { replace: true });
    } catch (x) {
      setError(errMsg(x));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-[1.1fr_1fr]">
      <div className="relative hidden overflow-hidden border-r border-slate-200 bg-gradient-to-b from-blue-50 via-white to-slate-50 p-14 text-slate-900 lg:flex lg:flex-col lg:justify-between">
        <div className="relative"><Link to="/" data-testid="login-home-link"><BrandMark logo={logo} dark className="text-2xl" /></Link></div>
        <div className="relative max-w-lg">
          <div className="mb-6 font-mono text-xs uppercase tracking-[0.3em] text-blue-700">FatturaPA · SdI · Multi studio</div>
          <h1 className="font-display text-5xl font-extrabold leading-[1.05] tracking-tight">
            Ogni fattura, ogni studio, <span className="text-blue-600">un solo archivio.</span>
          </h1>
          <p className="mt-6 text-base leading-relaxed text-slate-600">
            Emissione e ricezione XML, numerazione automatica, note di credito, scadenze e incassi. Gli studi scaricano gli XML dei propri clienti in un clic.
          </p>
        </div>
        <div className="relative grid grid-cols-3 gap-6 border-t border-slate-200 pt-6 font-mono text-xs text-slate-500">
          <div><div className="text-lg text-slate-900">FPR12</div>Formato B2B</div>
          <div><div className="text-lg text-slate-900">TD01·TD04</div>Fatture e NC</div>
          <div><div className="text-lg text-slate-900">ZIP</div>Export per studio</div>
        </div>
      </div>
      <div className="flex items-center justify-center bg-white p-6">
        <form onSubmit={submit} className="fade-up w-full max-w-sm space-y-6" data-testid="login-form">
          <div>
            <div className="mb-8 lg:hidden"><Link to="/" data-testid="login-home-link-mobile"><BrandMark logo={logo} dark className="text-2xl" /></Link></div>
            <h2 className="font-display text-3xl font-extrabold tracking-tight text-slate-900">Accedi</h2>
            <p className="mt-1 text-sm text-slate-500">Usa le credenziali fornite dal tuo studio.</p>
          </div>
          <Field label="Email o partita IVA">
            <Input data-testid="login-email-input" type="text" required autoComplete="username" placeholder="nome@email.it oppure IT01234567890" value={email} onChange={(e) => setEmail(e.target.value)} className="h-11 bg-white" />
          </Field>
          <Field label="Password">
            <Input data-testid="login-password-input" type="password" required autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} className="h-11 bg-white" />
          </Field>
          <div className="-mt-3 flex justify-end"><ForgotPasswordDialog /></div>
          {error && <div data-testid="login-error" className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</div>}
          <Button data-testid="login-submit-button" disabled={loading} className="h-11 w-full bg-blue-600 hover:bg-blue-700">
            {loading ? "Accesso in corso…" : "Entra"} <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <ShieldCheck className="h-4 w-4 text-emerald-600" /> Accesso riservato. Non hai un account? <Link to="/#richiesta" data-testid="login-request-link" className="font-semibold text-sky-700 underline">Richiedilo</Link>
          </div>
        </form>
      </div>
    </div>
  );
}
